import React, { useCallback, useContext, useEffect, useState } from 'react';
import Loading from '../../../components/Loading';
import STORE from '../../../store';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import CurrentUserContext from '../../../contexts/CurrentUserContext';
import useLockedDebouncedEffect from '../../../hooks/useLockedDebouncedEffect';
import ImpersonatorContext from '../../../contexts/ImpersonatorContext';
import getIdentifier from '../../../getIdentifier';

export const useContentSettings = ({
  userId,
  accountId,
}: {
  userId: string;
  accountId: string;
}) => {
  const [refreshKey, setRefreshKey] = useState<string>(getIdentifier());
  const [contentSettings, setContentSettings] = React.useState<
    UserManagement.ContentSettings | undefined
  >();
  const [args, setArgs] = useState<{
    userId: string;
    accountId: string;
    refreshKey: string;
  }>({
    userId,
    accountId,
    refreshKey,
  });
  useEffect(() => {
    setArgs({
      userId,
      accountId,
      refreshKey,
    });
  }, [accountId, userId, refreshKey]);

  const callback = useCallback(
    async ({
      userId,
      accountId,
    }: {
      userId: string;
      accountId: string;
    }): Promise<UserManagement.ContentSettings> => {
      setContentSettings(undefined);
      return STORE.userManagement
        .getUserContentSettingsRef({ accountId, userId })
        .get()
        .then((doc) => {
          if (!doc.exists) {
            return {
              mode: 'normal',
            };
          }
          const newSettings = doc.data();
          if (!newSettings) {
            return {
              mode: 'normal',
            };
          }

          return newSettings;
        });
    },
    [],
  );

  const responseHandler = useCallback(
    (newSettings: UserManagement.ContentSettings) => {
      setContentSettings(newSettings);
    },
    [],
  );

  useLockedDebouncedEffect({
    args,
    callback,
    responseHandler,
  });

  const refreshContentSettings = useCallback(() => {
    setRefreshKey(getIdentifier());
  }, []);

  return { contentSettings, refreshContentSettings };
};

export const CurrentUserContentSettingsContext = React.createContext<{
  contentSettings: UserManagement.ContentSettings;
  refreshContentSettings: () => void;
}>({
  contentSettings: { mode: 'normal' },
  refreshContentSettings: () => {},
});

const CurrentUserContentSettingsProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const currentUser = useContext(CurrentUserContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const userId = impersonatorId ? impersonatorId : currentUser.id;
  const { contentSettings, refreshContentSettings } = useContentSettings({
    userId,
    accountId: selectedAccountId,
  });

  if (!contentSettings) {
    return <Loading isBlocking />;
  }

  return (
    <CurrentUserContentSettingsContext.Provider
      value={{ contentSettings, refreshContentSettings }}
    >
      {children}
    </CurrentUserContentSettingsContext.Provider>
  );
};

export default CurrentUserContentSettingsProvider;
